// Dashboard Portal
// pages/Terms/TermsAcceptanceStep.js

import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningIcon from "@mui/icons-material/Warning";

/**
 * TermsAcceptanceStep Component
 * 
 * Handles terms acceptance during signup for both:
 * - Admins: Accept KlonIT platform terms
 * - Regular Users: Accept admin's custom terms
 * 
 * Props:
 * - isAdmin: boolean - Whether the user is an admin
 * - onComplete: function - Called when terms are accepted successfully
 * - onError: function - Called when there's an error
 * - isSetupMode: boolean - Whether in setup flow
 */
export default function TermsAcceptanceStep({ 
  isAdmin = false, 
  onComplete, 
  onError,
  isSetupMode = true 
}) {
  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [terms, setTerms] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState({});
  const [error, setError] = useState(null);
  const [platformTermsContent, setPlatformTermsContent] = useState(null);

  // Fetch terms on mount
  useEffect(() => {
    fetchTerms();
  }, [isAdmin]);

  /**
   * Fetch appropriate terms based on user role
   */
  const fetchTerms = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isAdmin) {
        // For admins, fetch KlonIT platform terms
        await fetchPlatformTerms();
      } else {
        // For regular users, fetch assigned custom terms
        await fetchUserTerms();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to load terms";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch KlonIT platform terms for admins
   */
  const fetchPlatformTerms = async () => {
    try {
      // Fetch content from KlonIT terms page
      const response = await fetch('https://klonit.ai/terms-of-use');
      const html = await response.text();
      
      // Extract main content (you might need to adjust this based on actual HTML structure)
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const mainContent = doc.querySelector('main') || doc.querySelector('body');
      
      setPlatformTermsContent(mainContent?.innerHTML || html);
      
      // Create a single term object for KlonIT terms
      setTerms([{
        id: 'klonit-platform-terms',
        title: 'KlonIT Terms of Use',
        description: 'Platform terms and conditions',
        file_url: 'https://klonit.ai/terms-of-use',
        is_platform_term: true
      }]);
      
      // Initialize acceptance state
      setAcceptedTerms({ 'klonit-platform-terms': false });
    } catch (err) {
      console.error('Error fetching platform terms:', err);
      
      // Fallback: Just show link to terms
      setTerms([{
        id: 'klonit-platform-terms',
        title: 'KlonIT Terms of Use',
        description: 'Please review our platform terms and conditions',
        file_url: 'https://klonit.ai/terms-of-use',
        is_platform_term: true
      }]);
      
      setAcceptedTerms({ 'klonit-platform-terms': false });
    }
  };

  /**
   * Fetch custom terms assigned to regular users
   */
  const fetchUserTerms = async () => {
    const response = await axiosInstance.get('/api/terms/pending');
    
    if (response.data.success) {
      const pendingTerms = response.data.pending_terms || [];
      setTerms(pendingTerms);
      
      // Initialize acceptance state for all terms
      const initialState = {};
      pendingTerms.forEach((term) => {
        initialState[term.id] = false;
      });
      setAcceptedTerms(initialState);
    }
  };

  /**
   * Handle checkbox change
   */
  const handleCheckboxChange = (termId) => {
    setAcceptedTerms((prev) => ({
      ...prev,
      [termId]: !prev[termId],
    }));
  };

  /**
   * Check if all terms are accepted
   */
  const allTermsAccepted = () => {
    return terms.length > 0 && terms.every((term) => acceptedTerms[term.id] === true);
  };

  const updateUserTermToLocalStorage = (data) => {
      const userStr = localStorage.getItem('user');
      if (userStr) {
          const userData = JSON.parse(userStr);
          userData.active_team.has_pending_terms = false;
          userData.active_team.pending_terms_count = 0;
          localStorage.setItem('user', JSON.stringify(userData));
          window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: userData  }));
          console.log("Local storage user profile updated.", data);
      }
  }

  /**
   * Handle terms acceptance
   */
  const handleAcceptTerms = async () => {
    if (!allTermsAccepted()) {
      setError("Please accept all terms to continue");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const termIds = terms.map((term) => term.id);

      if (!isAdmin) {
        // For regular users, accept custom terms
        await axiosInstance.post('/api/terms/accept', {
          term_ids: termIds,
        });

      }
      
      // Update Term in localstorage.
      updateUserTermToLocalStorage()
      // Call onComplete callback
      if (onComplete) {
        // Determine next step based on role
        const nextStep = isAdmin ? '/select-plan' : '/chatbots';
        onComplete(nextStep);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to accept terms";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Box textAlign="center" py={5}>
        <CircularProgress />
        <Typography mt={2}>Loading terms...</Typography>
      </Box>
    );
  }

  /**
   * Render no terms state
   */
  if (terms.length === 0) {
    return (
      <Alert severity="info">
        No terms to accept. You can continue to the next step.
      </Alert>
    );
  }

  /**
   * Main render
   */
  return (
    <Box>
      {/* Header */}
      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600}>
          Terms Acceptance Required
        </Typography>
        <Typography variant="body2">
          {isAdmin 
            ? "Please review and accept the KlonIT platform terms to continue setting up your account."
            : "Please review and accept the following terms to access your workspace."}
        </Typography>
      </Alert>

      {/* Terms List */}
      <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 3 }}>
        {terms.map((term, index) => (
          <Paper
            key={term.id}
            variant="outlined"
            sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}
          >
            {/* Term Header */}
            <Box display="flex" alignItems="start" justifyContent="space-between" mb={2}>
              <Box flex={1}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  {index + 1}. {term.title}
                </Typography>

                {term.description && (
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {term.description}
                  </Typography>
                )}

                {/* Link to view terms */}
                {term.file_url && (
                  <Link
                    href={term.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ 
                      display: "inline-flex", 
                      alignItems: "center", 
                      gap: 0.5,
                      color: 'primary.main' 
                    }}
                  >
                    <DescriptionIcon fontSize="small" />
                    View Full Document
                  </Link>
                )}
              </Box>
            </Box>

            {/* Platform terms preview (for admins) */}
            {term.is_platform_term && platformTermsContent && (
              <Box 
                sx={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  border: 1, 
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  bgcolor: 'background.paper'
                }}
              >
                <div 
                  dangerouslySetInnerHTML={{ __html: platformTermsContent }}
                  style={{ fontSize: '0.875rem' }}
                />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Acceptance Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTerms[term.id] || false}
                  onChange={() => handleCheckboxChange(term.id)}
                  color="primary"
                  disabled={submitting}
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  I have read and accept the terms stated in "{term.title}"
                </Typography>
              }
            />
          </Paper>
        ))}
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Important Notice */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600}>
          Important Notice
        </Typography>
        <Typography variant="body2">
          By accepting these terms, you acknowledge that you have read, understood, 
          and agree to be bound by the conditions outlined in each document. 
          {isAdmin && " These terms govern your use of the KlonIT platform."}
        </Typography>
      </Alert>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {terms.length} term{terms.length !== 1 ? 's' : ''} to accept
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={handleAcceptTerms}
          disabled={!allTermsAccepted() || submitting}
          size="large"
          startIcon={submitting ? <CircularProgress size={18} /> : <CheckCircleIcon />}
        >
          {submitting 
            ? "Accepting..." 
            : `Accept ${terms.length > 1 ? 'All ' : ''}Term${terms.length !== 1 ? 's' : ''} & Continue`}
        </Button>
      </Box>

      {/* Progress Indicator */}
      {!allTermsAccepted() && (
        <Box mt={2}>
          <Alert severity="warning" icon={false}>
            <Typography variant="caption">
              ⚠️ You must accept all terms to continue. 
              {terms.filter(t => !acceptedTerms[t.id]).length} remaining.
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
}